import { NewAutocomplete } from '@/components/ui/Autocomplete/NewAutocompleteInput'
import { BackButton } from '@/components/ui/BackButton'
import { BlockContainer } from '@/components/ui/BlockContainer'
import {
	ModuleContainer,
	ScrollableContent,
} from '@/components/ui/ModuleContainer'
import { TraficInfoOptionButton } from '@/components/ui/Schedules/TraficInfoOptionButton'
import { SwitchPMR } from '@/components/ui/SwitchPMR'
import { Title } from '@/components/ui/Title'
import { useModule } from '@/hooks/useModule'
import { useProtectedStringParam, useStringParam } from '@/hooks/useParam'
import {
	useSearchBarActions,
	useSetSearchModuleActions,
} from '@/hooks/useSearch'
import { useTranslator } from '@/hooks/useTranslator'
import { useSelector } from '@/stores/hooks'
import { getMetaRender } from '@/stores/meta'
import type { CustomLocationHook } from '@/types/useCustomLocation'
import { isIsoDateTime } from '@lc/libdates'
import { classes } from '@lc/libreact'
import { useRef, useState } from 'react'
import { useLocation } from 'wouter'
import styles from './Lines.scss'
import { AreaNextTrips } from './ui/AreaNextTrips'
import { LinesGroups } from './ui/LinesGroups'

function ModuleOptions() {
	const defaultDateTime = useSelector(getMetaRender)!
	const [datetime, setDatetime] = useProtectedStringParam(
		'date',
		defaultDateTime,
		isIsoDateTime,
	)

	const [isCalendarModalVisible, setIsCalendarModalVisible] = useState(false)
	const [calendarActiveButton, setCalendarActiveButton] = useState<
		'current' | 'coming'
	>('current')
	const toggleActiveCalendarButton = (
		calendarButton: 'current' | 'coming',
	) => {
		setCalendarActiveButton(calendarButton)
	}

	const handleCalendarButtonClick = () => {
		setIsCalendarModalVisible(prevState => !prevState)
	}

	return (
		<section className={styles.headerLine2}>
			<TraficInfoOptionButton
				date={datetime}
				defaultDate={defaultDateTime}
				setDate={setDatetime}
				handleClick={handleCalendarButtonClick}
				isCalendarModalVisible={isCalendarModalVisible}
				calendarActiveButton={calendarActiveButton}
				setActiveCalendarButton={toggleActiveCalendarButton}
			/>
			<SwitchPMR />
		</section>
	)
}

export default function Lines() {
	const [, setLocation] = useLocation<CustomLocationHook>()
	const { path } = useModule()
	const translator = useTranslator()
	const [area, setArea] = useStringParam('area', '')
	const [name, setName] = useStringParam('name', '')
	const formRef = useRef<HTMLFormElement>(null)

	const { acceptSearch } = useSetSearchModuleActions({
		acceptSearch(result) {
			switch (result.type) {
				case 'area':
					setArea(result.area.id)
					setName(result.area.name)
					break

				case 'line':
					setLocation(`${path}/${result.line.id}/forward`, {
						removeParams: ['area', 'name', 'coord'],
					})
					break
			}
		},
		//TODO: DEBOUNCE
		clearSearch() {
			setArea('')
			setName('')
		},
	})

	//TODO: DEBOUNCE
	const { clearSearch } = useSearchBarActions()

	const hasResults = !!area

	return (
		<ModuleContainer>
			<ScrollableContent>
				<section className={styles.header}>
					{hasResults && (
						<BackButton
							onClick={() => {
								setLocation(path, {
									removeParams: ['area', 'name', 'coord'],
								})
							}}
						/>
					)}
					<BlockContainer className={styles.headerContainer}>
						<form className={classes(styles.form)} ref={formRef}>
							<NewAutocomplete
								types={['line', 'area']}
								search={name}
								onSelect={acceptSearch}
								onChange={clearSearch}
								placeholder={translator.translate(
									'autocomplete-lines:placeholder',
								)}
								noGeoloc
							/>
						</form>
					</BlockContainer>
					<ModuleOptions />
				</section>
				{area ? (
					<section className={styles.nextTrips}>
						<Title
							className={styles.nextTripsTitle}
							level="h2"
							title={translator.translate('next-departures')}
							color={'black'}
						/>
						<AreaNextTrips area={area} />
					</section>
				) : (
					<LinesGroups />
				)}
			</ScrollableContent>
		</ModuleContainer>
	)
}
